<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-container class="pa-0">
          <v-alert type="info" dense v-if="!userDataTable.loading && 0 >= userDataTable.total">
            We were unable to find any matching users. Try modifying your search criteria
          </v-alert>
          <v-row>
            <v-col>
              <v-select
                id="user-list-content--search-type"
                v-model="searchAttributeKey"
                :items="searchAttributeKeyItems"
                single-line
                persistent-hint
                filled
                dense
                @change="changeSearchAttributeKey"
              >
                <template slot="item" slot-scope="{ item }">
                  <v-list-item>
                    <v-list-item-action>
                      <v-checkbox
                        :ripple="false"
                        dense
                        :input-value="searchAttributeKeySelected(item.value)"
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.text }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-5"></v-divider>
                </template>
              </v-select>
            </v-col>
            <v-col>
              <v-text-field
                v-if="searchAttributeValueInputDisplayed"
                v-model="searchAttributeValue"
                append-icon="mdi-magnify"
                single-line
                hide-details
                dense
                filled
                @click:append="submitUserSearch"
                @keyup.enter="submitUserSearch"
              ></v-text-field>
            </v-col>
            <v-col class="text-right">
              <v-btn class="add-user-btn" color="primary" href="/users/new">
                <v-icon left>mdi-account-plus</v-icon>
                Add User
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
        <v-container class="pa-0">
          <v-data-table
            :headers="userDataTable.headers"
            :items="userDataTable.items"
            :server-items-length="userDataTable.total"
            :loading="userDataTable.loading"
            :options.sync="userDataTableOptionsSync"
            :footer-props="userDataTable.footerProps"
            :hide-default-footer="true"
            no-data-text="No user found"
            class="elevation-1"
          >
            <template v-slot:item.creationDatetime="{ item }">
              {{ getCreationDatetime(item.creationDatetime) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                :class="`testclass`"
                :id="`user-list-content--user-update-button--${getMd5(item.email)}`"
                :href="`/users/info?email=${encodeURIComponent(item.email)}`"
                text
                icon
                small
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                :id="`user-list-content--user-invite-resend-button--${getMd5(item.email)}`"
                @click="resendUserInvite(item.email)"
                text
                icon
                small
                :loading="isResendInviteLoading(item.email)"
                :disabled="item.status === 'CONFIRMED' || !item.enabled"
              >
                <v-icon>mdi-account-key</v-icon>
              </v-btn>
              <v-btn
                :id="`user-list-content--user-deletion-button--${getMd5(item.email)}`"
                @click="displayUserDeletionDialog(item.email)"
                text
                icon
                small
              >
                <v-icon>mdi-trash-can</v-icon>
              </v-btn>
              <v-btn
                :id="`user-list-content--user-enablement-button--${getMd5(item.email)}`"
                @click="displayUserEnablementDialog(item.email, !item.enabled)"
                text
                icon
                small
              >
                <v-icon v-if="item.enabled">mdi-checkbox-marked-outline</v-icon>
                <v-icon v-if="!item.enabled">mdi-checkbox-blank-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-container>
      </v-col>
    </v-row>
    <div class="text-center v-pagination">
      <v-btn
        class="mx-2"
        depressed
        fab
        x-small
        :disabled="1 === userDataTableOptionsSync.page || userDataTableFetchActionInProgress"
        @click="changeUserListPage(false)"
      >
        <v-icon dark>mdi-chevron-left</v-icon>
      </v-btn>
      <v-chip outlined>{{ userDataTableOptionsSync.page }}</v-chip>
      <v-btn
        class="mx-2"
        depressed
        fab
        x-small
        :disabled="0 === nextPage || userDataTableFetchActionInProgress"
        @click="changeUserListPage(true)"
      >
        <v-icon dark>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
    <UserListErrorDialog :ref="userListErrorDialogRef"></UserListErrorDialog>
    <UserListGenericDialog :ref="userListGenericDialogRef"></UserListGenericDialog>
    <UserListSuccessDialog :ref="userListSuccessDialogRef"></UserListSuccessDialog>
  </v-container>
</template>

<script>
import UserListErrorDialog from './UserListErrorDialog';
import UserListGenericDialog from './UserListGenericDialog';
import UserListSuccessDialog from './UserListSuccessDialog';

import storeAction from '../lib/const/storeAction';
import systemEvent from '../lib/const/systemEvent';

export default {
  components: {
    UserListErrorDialog,
    UserListGenericDialog,
    UserListSuccessDialog
  },

  data() {
    return {
      userListErrorDialogRef: 'userListErrorDialog',
      userListGenericDialogRef: 'userListGenericDialog',
      userListSuccessDialogRef: 'userListSuccessDialog',
      functionCommonAdapter: false,
      selectedUserEmail: '',

      userDataTable: {
        footerProps: {
          itemsPerPageOptions: [60]
        },
        headers: [
          {
            text: 'Given Name',
            value: 'givenName',
            sortable: false
          },
          {
            text: 'Surname',
            value: 'surname',
            sortable: false
          },
          {
            text: 'Email',
            value: 'email',
            sortable: false
          },
          {
            text: 'Active',
            value: 'enabled',
            sortable: false
          },
          {
            text: 'Creation Datetime',
            value: 'creationDatetime',
            sortable: false
          },
          {
            text: 'Actions',
            value: 'actions',
            sortable: false,
            width: 160
          }
        ],
        items: [],
        loading: false,
        total: 0,
        loadingItem: ''
      },
      userDataTableOptionsSync: {
        page: 1,
        itemsPerPage: 60
      },
      userDataTableAuthActionEnabled: false,
      userDataTableFetchActionInProgress: false,
      nextPage: 0,
      searchAttributeKey: '',
      searchAttributeValue: '',
      searchAttributeKeyItems: [
        {
          text: 'All Users',
          value: ''
        },
        {
          text: 'Given Name',
          value: 'givenName'
        },
        {
          text: 'Surname',
          value: 'surname'
        },
        {
          text: 'Email',
          value: 'email'
        },
        {
          text: 'Active Users',
          value: 'activeUsers'
        },
        {
          text: 'Deactivated Users',
          value: 'deactivatedUsers'
        }
      ]
    };
  },

  methods: {
    displayUserDeletionDialog(userEmail) {
      this.$refs[this.userListGenericDialogRef].displayUserDeletionDialog(userEmail);
    },

    resendUserInvite(userEmail) {
      this.selectedUserEmail = userEmail;
      this.functionCommonAdapter.push([this.postUserInviteResend]);
      this.$app.authCommonAdapter.enforceUserAuthStrategy(this.$route, false);
    },

    isResendInviteLoading(selectedEmail) {
      return this.userDataTable.loadingItem === selectedEmail;
    },

    displayUserEnablementDialog(userEmail, userEnabled) {
      this.$refs[this.userListGenericDialogRef].displayUserEnablementDialog(userEmail, userEnabled);
    },

    getCreationDatetime(utc0Datetime) {
      return this.$app.moment(utc0Datetime).format('MMMM D, YYYY H:mm:ss');
    },

    getMd5(str) {
      return this.$app.crypto.createHash('md5').update(str).digest('hex');
    },

    async getUserList() {
      if (!this.userDataTableFetchActionInProgress) {
        this.userDataTable.loading = true;
        this.userDataTableFetchActionInProgress = true;

        const dataHash = {
          page: this.userDataTableOptionsSync.page
        };

        if (this.searchAttributeKey) {
          if (this.searchAttributeKey === 'activeUsers') {
            dataHash['s-attribute-key'] = 'status';
            dataHash['s-attribute-value'] = 'Enabled';
          } else if (this.searchAttributeKey === 'deactivatedUsers') {
            dataHash['s-attribute-key'] = 'status';
            dataHash['s-attribute-value'] = 'Disabled';
          } else if (this.searchAttributeValue) {
            dataHash['s-attribute-key'] = this.searchAttributeKey;
            dataHash['s-attribute-value'] = this.searchAttributeValue;
          }
        }

        const userListWrapper = await this.$store.dispatch(storeAction.GET_USER_LIST, dataHash);
        if (userListWrapper) {
          this.userDataTable.items = userListWrapper.data ? userListWrapper.data : [];
          this.userDataTable.total = userListWrapper.total ? userListWrapper.total : 0;
          this.nextPage = userListWrapper.nextPage ? userListWrapper.nextPage : 0;
        }

        this.userDataTableFetchActionInProgress = false;
        this.userDataTable.loading = false;
      }
    },

    changeUserListPage(forwardDirection) {
      if (forwardDirection) {
        this.userDataTableOptionsSync.page += 1;
      } else {
        this.userDataTableOptionsSync.page -= 1;
      }

      if (this.userDataTableAuthActionEnabled) {
        this.functionCommonAdapter.push([this.getUserList]);
        this.$app.authCommonAdapter.enforceUserAuthStrategy(this.$route, false);
      }
    },

    changeSearchAttributeKey() {
      const displayItems = ['', 'activeUsers', 'deactivatedUsers'];

      if (displayItems.some((x) => x === this.searchAttributeKey)) {
        this.userDataTableOptionsSync.page = 1;
        this.searchAttributeValue = '';

        if (this.userDataTableAuthActionEnabled) {
          this.functionCommonAdapter.push([this.getUserList]);
          this.$app.authCommonAdapter.enforceUserAuthStrategy(this.$route, false);
        }
      }
    },

    submitUserSearch() {
      this.userDataTableOptionsSync.page = 1;

      if (this.userDataTableAuthActionEnabled) {
        this.functionCommonAdapter.push([this.getUserList]);
        this.$app.authCommonAdapter.enforceUserAuthStrategy(this.$route, false);
      }
    },

    async refreshUserList(userListRefreshed) {
      if (this.userDataTableAuthActionEnabled && userListRefreshed) {
        this.functionCommonAdapter.push([this.getUserList]);
        this.$app.authCommonAdapter.enforceUserAuthStrategy(this.$route, false);
      }
    },

    async handleAuthStrategyEnforcementResponse(sessionUser) {
      if (sessionUser) {
        this.userDataTableAuthActionEnabled = true;
        this.functionCommonAdapter.execute();
      } else {
        this.functionCommonAdapter.push([this.getUserList]);
      }
    },

    async postUserInviteResend() {
      this.userDataTable.loadingItem = this.selectedUserEmail;
      const postUserInviteResendData = {
        email: this.selectedUserEmail
      };
      const udUser = await this.$store
        .dispatch(storeAction.POST_USER_INVITE_RESEND, postUserInviteResendData)
        .catch((err) => {
          const priorityHtmlErrorMessageHash = {
            409: "<span>User's email has already been verified.</span>"
          };
          const errorDialogArgHash = {
            errorDialogTitle: 'User Invite Failed',
            errorDialogBody: this.$app.userCommon.getApiHtmlErrorMessage(
              err,
              priorityHtmlErrorMessageHash
            ),
            errorDialogDisplayed: true
          };
          this.userDataTable.loadingItem = '';
          this.$app.eventBus.$emit(systemEvent.USER_LIST_ERROR_DIALOG_CALLED, errorDialogArgHash);
        });

      if (udUser) {
        this.genericDialogDisplayed = false;
        const successDialogArgHash = {
          successDialogTitle: 'Success!',
          successDialogBody: 'User verification email has been sent.',
          successDialogDisplayed: true
        };
        this.userDataTable.loadingItem = '';
        this.$app.eventBus.$emit(systemEvent.USER_LIST_SUCCESS_DIALOG_CALLED, successDialogArgHash);
      }
    }
  },

  async mounted() {
    const that = this;

    this.functionCommonAdapter = new this.$app.functionCommon.Adapter();

    this.$app.eventBus.$on(
      systemEvent.AUTH_STRATEGY_ENFORCEMENT_DONE,
      that.handleAuthStrategyEnforcementResponse
    );
    this.$app.eventBus.$on(systemEvent.USER_LIST_GENERIC_DIALOG_ACTION_DONE, that.refreshUserList);

    this.userDataTable.loading = true;
  },

  beforeDestroy() {
    this.$app.eventBus.$off(systemEvent.AUTH_STRATEGY_ENFORCEMENT_DONE);
    this.$app.eventBus.$off(systemEvent.USER_LIST_GENERIC_DIALOG_ACTION_DONE);
  },

  computed: {
    searchAttributeKeySelected() {
      // eslint-disable-next-line func-names
      return function (value) {
        return this.searchAttributeKey === value;
      };
    },
    searchAttributeValueInputDisplayed() {
      const displayItems = ['givenName', 'surname', 'email'];

      return displayItems.some((x) => x === this.searchAttributeKey);
    }
  },

  watch: {
    userDataTableOptionsSync: {
      async handler() {
        if (this.userDataTableAuthActionEnabled) {
          this.functionCommonAdapter.push([this.getUserList]);
          this.$app.authCommonAdapter.enforceUserAuthStrategy(this.$route, false);
        }
      }
    }
  }
};
</script>
<style scoped>
.theme--light.v-btn:not(.v-btn--round).v-size--default.add-user-btn {
  padding: 8px 16px;
}
</style>
